import {
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
  AfterContentChecked,
  ElementRef,
  inject
} from '@angular/core';
import {
  ApiServiceService,
  NotificationService,
  LoggingService,
  ValidateFormDataService
} from '@oneeducation/app-core';
import { exclusionsModel, reasonsModel } from '@exclusionmodule/models';
import { EXCLUSION_ENDPOINT, ADMIN_ENDPOINT } from '@shared/properties';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LookupModel } from '@shared/models';
import { DirtyComponent } from '@shared/models';
import { DataLossService, AccessibilityService } from '@shared/services';
import { Select } from 'primeng/select';
import { Listbox } from 'primeng/listbox';

@Component({
  selector: 'app-exclusion-reasons',
  templateUrl: './exclusion-reasons.component.html',
  styleUrls: ['./exclusion-reasons.component.scss'],
  standalone: false
})
export class ExclusionReasonsComponent
  implements OnInit, DirtyComponent, AfterContentChecked
{
  private apiService = inject(ApiServiceService);
  private notificationService = inject(NotificationService);
  private validateFormDataService = inject(ValidateFormDataService);
  private loggingService = inject(LoggingService);
  private dataLossService = inject(DataLossService);
  private accessibilityService = inject(AccessibilityService);

  @Input() exclusionId: number;
  @Input() pupilName: string;
  @Input() isCreateExclusion: boolean;
  @Input() isStudentOffRoll: boolean;
  @Input() isOffRollEditingEnabled: boolean;
  @Output() isChildFormValid: EventEmitter<boolean> =
    new EventEmitter<boolean>();
  @ViewChild('dropdownPrimaryReason') dropdownPrimaryReason: Select;
  @ViewChild('additionalReasonsListBox') additionalReasonsListBox: Listbox;
  @ViewChild('editReasonsButton') editReasonsButton: ElementRef;
  reasons: reasonsModel[];
  exclusions: exclusionsModel;
  primaryReason: string;
  secondaryReason: string[];
  secondaryReasonCheck = false;
  primaryReasonText: string;
  allReasonsList: LookupModel[];
  filteredReasonsList: LookupModel[];
  additionalReasonList: LookupModel[];
  isEditing: boolean;
  selectedPrimaryReason: string;
  userFields: FormGroup;
  countSelectedAddnReasons = 0;
  primaryReasonInactive: boolean;
  isSetFocusOnEditReasonsButton = false;
  componentDetail = {
    componentName: 'Reasons',
    formNameToCheckForDirtyness: 'userFields'
  };

  ngOnInit() {
    if (!this.isCreateExclusion) {
      this.getExclusionReasons();
    }
    this.setUserFields(true);
    this.getLookups();
  }

  getExclusionReasons() {
    this.primaryReasonInactive = false;
    this.apiService
      .getAll<
        exclusionsModel[]
      >(EXCLUSION_ENDPOINT.GetExclusion.replace('{exclusionId}', this.exclusionId?.toString()), '')
      .subscribe({
        next: (x: any) => {
          this.reasons = x.exclusion.reasons;
          if (this.reasons != null && this.reasons.length > 0) {
            //TODO - Check - Need to assign PrimaryReason directly.
            this.primaryReason = this.reasons.filter(
              (x) => x.position == 1
            )[0].reason;

            this.primaryReasonText =
              this.pupilName.substr(0, this.pupilName.indexOf(' ')) +
              ' was primarily excluded for ' +
              this.primaryReason.toLowerCase() +
              '.';

            this.secondaryReason = this.reasons
              .filter((x) => x.position != 1)
              .map((x) => x.reason);
          }
          if (this.secondaryReason != null && this.secondaryReason.length > 0) {
            this.secondaryReasonCheck = true;
            this.countSelectedAddnReasons = this.secondaryReason.length;
          } else {
            this.secondaryReasonCheck = false;
          }

          this.setUserFields(this.primaryReasonInactive);
          this.getLookups();
        },
        error: (x: any) => {
          if (x.error.statusCode === 404) {
            this.notificationService.showInformation(
              'No exclusion reasons found'
            );
          } else {
            this.notificationService.showError(
              'Error getting exclusion reasons'
            );
          }
        }
      });
  }

  get f() {
    return this.userFields.controls;
  }

  getLookups() {
    //0309-Exclusion Reason
    const tableid = '0309';
    this.apiService
      .getAll<
        LookupModel[]
      >(ADMIN_ENDPOINT.GetLookups.replace('{tableId}', tableid) + '?active=True', '')
      .subscribe({
        next: (x: any) => {
          this.allReasonsList = x.lookups;
          this.matchPrimaryReason();
          this.additionalReasonList = x.lookups.filter(
            (x) => x.description !== this.primaryReason
          );
          this.filteredReasonsList = this.selectedPrimaryReason
            ? x.lookups.filter(
                (x) => x.description !== this.selectedPrimaryReason
              )
            : this.additionalReasonList;
        },
        error: (x: any) => {
          if (x.error.statusCode === 404) {
            this.notificationService.showInformation('No lookups found');
          } else {
            this.notificationService.showError('Error getting lookups');
          }
        }
      });
  }

  editReasons() {
    this.isEditing = true;
    setTimeout(() => {
      if (this.dropdownPrimaryReason) {
        this.dropdownPrimaryReason.focus();
      }
    }, 0);
  }

  cancelReasons() {
    this.isSetFocusOnEditReasonsButton = true;
    if (this.dataLossService.checkForUnsavedChanges(this)) {
      this.resetToOriginal();
    }
  }

  resetToOriginal() {
    this.isEditing = false;
    this.ngOnInit();
  }

  changePrimaryReasonOptions(event) {
    this.selectedPrimaryReason = event.value;
    this.filteredReasonsList = this.selectedPrimaryReason
      ? this.allReasonsList.filter(
          (x) => x.description !== this.selectedPrimaryReason
        )
      : this.additionalReasonList;
    this.removeItem(this.selectedPrimaryReason);
    this.countSelectedAddnReasons =
      this.userFields.value.additionalReason != null
        ? this.userFields.value.additionalReason.length
        : 0;
    this.selectedPrimaryReason = '';
  }

  removeItem(value) {
    const index: number =
      this.userFields.value.additionalReason != null
        ? this.userFields.value.additionalReason.indexOf(value)
        : -1;
    if (index !== -1) {
      this.userFields.value.additionalReason.splice(index, 1);
    }
  }

  SaveReasons() {
    setTimeout(() => {
      if (!this.userFields.valid) {
        this.validateFormDataService.validateAllFormFields(this.userFields);
        return;
      }
      this.exclusions = new exclusionsModel();
      this.exclusions.reasons = [];
      this.exclusions.reasons.push({
        reason: this.userFields.value.primaryReason
      });
      if (
        this.userFields.value.additionalReason !==
        this.userFields.value.primaryReason
      ) {
        this.userFields.value.additionalReason.forEach(
          (x) =>
            x !== this.userFields.value.primaryReason &&
            this.exclusions.reasons.push({
              reason: x
            })
        );
      }
      this.apiService
        .put(
          EXCLUSION_ENDPOINT.UpdateExclusionReasons.replace(
            '{exclusionId}',
            this.exclusionId?.toString()
          ),
          this.exclusions
        )
        .subscribe({
          next: () => {
            this.dataLossService.removeComponentFromUnsavedChanges(this);
            this.notificationService.showSuccess(
              'Exclusion reason(s) updated successfully.'
            );
            this.resetToOriginal();
          },
          error: (x: any) => {
            if (x.error !== null && x.error !== '') {
              this.notificationService.showError(
                'Something went wrong, please try again.'
              );
              this.loggingService?.logError(x);
            }
          }
        });
    }, 1000);

    this.isSetFocusOnEditReasonsButton = true;
  }

  countAdditionalReasons(event) {
    this.countSelectedAddnReasons = event.value.length;
  }

  matchPrimaryReason() {
    if (this.primaryReason !== '') {
      const matchPrimaryReason = this.allReasonsList.filter(
        (x) => x.description === this.primaryReason
      );
      if (matchPrimaryReason.length === 0) {
        this.primaryReasonInactive = true;
        this.userFields.value.primaryReason = '';
        this.setUserFields(this.primaryReasonInactive);
      }
    }
  }

  setUserFields(event) {
    if (event === true) {
      this.primaryReason = '';
    }
    this.userFields = new FormGroup({
      primaryReason: new FormControl<string>(this.primaryReason, [
        Validators.required
      ]),
      additionalReason: new FormControl<string[]>(this.secondaryReason)
    });

    this.userFields.valueChanges.subscribe(() => {
      this.dataLossService.addComponentToCheckUnsavedChanges(this);
    });
  }

  validateForm() {
    this.validateFormDataService.validateAllFormFields(this.userFields);
    if (this.userFields.valid) {
      this.isChildFormValid.emit(true);
    } else {
      this.isChildFormValid.emit(false);
    }
  }

  resetData() {
    //Reset Data method is required in Unsaved changes template
  }
  canDeactivate() {
    return this.dataLossService.isDirty();
  }

  // Set the attributes of the additional reason dropdown
  private setAttributesForAdditionalReasonListBox() {
    if (this.additionalReasonsListBox) {
      const props = new Map<string, string>();
      props.set('title', 'Additional exclusion reasons');
      this.accessibilityService.setListBoxListAttributes(
        this.additionalReasonsListBox,
        props
      );

      const attributeMap = new Map();
      attributeMap.set('aria-label', 'Additional exclusion reasons');
      this.accessibilityService.setListBoxLabelAttributes(
        this.additionalReasonsListBox,
        attributeMap
      );
    }
  }

  // Set the attributes of the primary reason dropdown
  private setAttributesForPrimaryReasonDropDown() {
    if (this.dropdownPrimaryReason) {
      const props = new Map<string, string>();
      props.set('title', 'Exclusion reasons');
      this.accessibilityService.setDropDownItemsAttributes(
        this.dropdownPrimaryReason,
        props
      );
      // set value to aria-activedescendant attribute
      this.accessibilityService.setDropDownAriaActiveDescendant(
        this.dropdownPrimaryReason,
        'primaryReason'
      );
    }
  }

  // Set the multiselect attributes of the reasons multiselect dropdown
  private setMultiselectAttributeForReasonsListBox() {
    this.accessibilityService.listboxAriaMultiSelectableSetTrue(
      this.additionalReasonsListBox
    );
  }

  ngAfterContentChecked() {
    this.setFocusOnEditReasonsButton();
    this.setAttributesForAdditionalReasonListBox();
    this.setAttributesForPrimaryReasonDropDown();
    this.setMultiselectAttributeForReasonsListBox();
    this.setAccessibleNameForDropdownButtons();
    this.setLandmarkRoleForDropdowns();
    this.setTabindexInScrollableRegionForDropdDowns();
    this.removeRoleAttributeForDropDowns();
    this.removeAriaExpandedAttributeForDropDowns();
  }

  // Set focus on edit reasons button
  private setFocusOnEditReasonsButton() {
    if (this.editReasonsButton && this.isSetFocusOnEditReasonsButton) {
      this.editReasonsButton.nativeElement.focus();
      this.isSetFocusOnEditReasonsButton = false;
    }
  }

  // Ensures every ARIA button, link and menu item has an accessible name
  private setAccessibleNameForDropdownButtons() {
    if (this.dropdownPrimaryReason) {
      this.accessibilityService.setAccessibleNameForDropdownMenuButton(
        this.dropdownPrimaryReason,
        'Select primary reason'
      );
    }
  }

  // Ensures all page content is contained by landmarks
  private setLandmarkRoleForDropdowns() {
    if (this.dropdownPrimaryReason) {
      this.accessibilityService.setLandmarkRoleForDropDown(
        this.dropdownPrimaryReason
      );
    }
  }

  // Ensure elements that have scrollable content are accessible by keyboard
  private setTabindexInScrollableRegionForDropdDowns() {
    if (this.dropdownPrimaryReason) {
      this.accessibilityService.setTabindexInScrollableRegionForDropdDown(
        this.dropdownPrimaryReason
      );
    }
  }

  // Ensures role attribute has an appropriate value for the element
  private removeRoleAttributeForDropDowns() {
    if (this.dropdownPrimaryReason) {
      this.accessibilityService.removeRoleAttributeForDropDown(
        this.dropdownPrimaryReason
      );
    }
  }

  // Remove aria -expanded attribute
  private removeAriaExpandedAttributeForDropDowns() {
    if (this.dropdownPrimaryReason) {
      this.accessibilityService.removeAriaExpandedAttribute(
        this.dropdownPrimaryReason
      );
    }
  }
}
